import {
  LEAD_STATUSES,
  PROPERTY_TYPES,
  getStatusStyle,
} from "../constants/leadConstants";
import { PRODUCT_DISPLAY_NAME } from "../../../shared/constants/branding";

const SOURCE_TYPES = [
  { id: 1, name: "Facebook" },
  { id: 2, name: "Instagram" },
  { id: 3, name: "Reference" },
];

const escHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatStatusLabel = (name) => {
  if (!name || name === "Unknown") return "Unknown";
  return name.replace(/([A-Z])/g, " $1").trim();
};

const fmtDate = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const fmtDateTime = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

const field = (label, value) => `
  <div class="field">
    <div class="field-label">${escHtml(label)}</div>
    <div class="field-value">${escHtml(value || "—")}</div>
  </div>`;

const resolveAgency = (agency = {}) => ({
  name: agency.agencyName ?? agency.AgencyName ?? "",
  logo: agency.agencyLogo ?? agency.AgencyLogo ?? "",
});

const resolveLeadMeta = (leadassigned) => {
  const lead = leadassigned?.lead ?? {};
  const user = leadassigned?.user ?? {};

  const sourceType = lead.sourceTypeId
    ? SOURCE_TYPES.find((s) => s.id === Number(lead.sourceTypeId))
    : null;
  const sourceTypeName =
    sourceType?.name ?? lead.source ?? "—";

  const statusEntry = LEAD_STATUSES.find(
    (s) => s.id === Number(leadassigned?.leadStatusId),
  );
  const statusName = statusEntry?.name ?? "Unknown";
  const statusStyle = getStatusStyle(statusName);

  const propertyType = PROPERTY_TYPES.find(
    (p) => p.id === Number(leadassigned?.propertyTypeId),
  );
  const propertyTypeName = propertyType?.name ?? "—";

  const leadName = [lead.firstName, lead.lastName]
    .filter(Boolean)
    .join(" ")
    .trim() || "Unknown Lead";

  const employeeName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim() || "—";

  return {
    lead,
    leadName,
    leadCode: lead.leadCode ?? lead.LeadCode ?? "",
    email: lead.email ?? "—",
    phone: lead.contactNumber ?? "—",
    property: lead.property ?? "—",
    address: lead.address ?? "—",
    sourceTypeName,
    statusName,
    statusLabel: formatStatusLabel(statusName),
    statusStyle,
    propertyTypeName,
    employeeName,
    employeeEmail: user.email ?? "—",
    assignedDate: fmtDateTime(leadassigned?.leadAssignedDate),
    followUpDate: fmtDate(leadassigned?.followUpDate),
    nextUpdate: fmtDate(leadassigned?.nextUpdate),
    startDate: fmtDateTime(lead?.startDate),
    comments: leadassigned?.conversationComment ?? "",
    refId: leadassigned?.id ?? lead?.id ?? "—",
  };
};

export function buildLeadPrintHtml(leadassigned, agency = {}) {
  const { name: agencyName, logo: agencyLogo } = resolveAgency(agency);
  const meta = resolveLeadMeta(leadassigned);
  const generatedAt = fmtDateTime(new Date());
  const logoHtml = agencyLogo
    ? `<img class="agency-logo" src="data:image/png;base64,${agencyLogo}" alt="${escHtml(agencyName || "Agency")}" />`
    : `<div class="agency-logo-fallback">${escHtml((agencyName || "A").charAt(0).toUpperCase())}</div>`;

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Lead Report — ${escHtml(meta.leadName)}</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body {
        font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
        color: #1e293b;
        background: #f1f5f9;
        padding: 24px;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .page {
        max-width: 820px;
        margin: 0 auto;
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 8px 32px rgba(15, 23, 42, 0.1);
        overflow: hidden;
      }
      .header {
        background: linear-gradient(135deg, #312e81 0%, #4f46e5 45%, #6366f1 100%);
        color: #fff;
        padding: 28px 32px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
      }
      .header-left { display: flex; align-items: center; gap: 16px; min-width: 0; }
      .agency-logo {
        width: 58px;
        height: 58px;
        border-radius: 12px;
        object-fit: contain;
        background: rgba(255, 255, 255, 0.95);
        padding: 5px;
        flex-shrink: 0;
      }
      .agency-logo-fallback {
        width: 58px;
        height: 58px;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        font-weight: 700;
        flex-shrink: 0;
      }
      .agency-name {
        font-size: 21px;
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 1.2;
        word-break: break-word;
      }
      .agency-sub { font-size: 12px; opacity: 0.85; margin-top: 3px; }
      .doc-type { text-align: right; flex-shrink: 0; }
      .doc-type h1 { font-size: 17px; font-weight: 600; letter-spacing: 0.02em; }
      .doc-ref { font-size: 12px; opacity: 0.9; margin-top: 5px; }
      .body { padding: 28px 32px 20px; }
      .lead-hero {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 26px;
        padding-bottom: 22px;
        border-bottom: 2px solid #f1f5f9;
      }
      .lead-name { font-size: 26px; font-weight: 700; color: #0f172a; line-height: 1.2; }
      .lead-code { font-size: 13px; color: #64748b; margin-top: 6px; }
      .status-badge {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 8px 14px;
        border-radius: 999px;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        background: ${meta.statusStyle.bg};
        color: ${meta.statusStyle.color};
      }
      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${meta.statusStyle.dot};
      }
      .section { margin-bottom: 22px; }
      .section-title {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #4f46e5;
        margin-bottom: 12px;
      }
      .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
      .field {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 12px 14px;
      }
      .field-label {
        font-size: 10px;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        margin-bottom: 5px;
        font-weight: 600;
      }
      .field-value {
        font-size: 14px;
        font-weight: 600;
        color: #1e293b;
        word-break: break-word;
        line-height: 1.4;
      }
      .comments-box {
        background: #fffbeb;
        border: 1px solid #fde68a;
        border-radius: 10px;
        padding: 14px 16px;
        font-size: 14px;
        line-height: 1.65;
        color: #78350f;
        min-height: 56px;
        white-space: pre-wrap;
        word-break: break-word;
      }
      .comments-empty { color: #a8a29e; font-style: italic; }
      .footer {
        background: #f8fafc;
        border-top: 1px solid #e2e8f0;
        padding: 14px 32px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        font-size: 11px;
        color: #94a3b8;
      }
      .footer-note { max-width: 420px; line-height: 1.5; }
      @media print {
        body { background: #fff; padding: 0; }
        .page { box-shadow: none; border-radius: 0; max-width: 100%; }
        @page { margin: 10mm; size: A4; }
      }
      @media (max-width: 640px) {
        .header { flex-direction: column; align-items: flex-start; }
        .doc-type { text-align: left; }
        .grid-2, .grid-3 { grid-template-columns: 1fr; }
        .lead-hero { flex-direction: column; }
      }
    </style>
  </head>
  <body>
    <div class="page">
      <header class="header">
        <div class="header-left">
          ${logoHtml}
          <div>
            <div class="agency-name">${escHtml(agencyName || "Real Estate Agency")}</div>
            <div class="agency-sub">Powered by ${escHtml(PRODUCT_DISPLAY_NAME)}</div>
          </div>
        </div>
        <div class="doc-type">
          <h1>Lead Summary Report</h1>
          <div class="doc-ref">Ref #${escHtml(meta.refId)}${meta.leadCode ? ` · ${escHtml(meta.leadCode)}` : ""}</div>
        </div>
      </header>

      <main class="body">
        <div class="lead-hero">
          <div>
            <div class="lead-name">${escHtml(meta.leadName)}</div>
            ${meta.leadCode ? `<div class="lead-code">Lead Code: ${escHtml(meta.leadCode)}</div>` : ""}
          </div>
          <span class="status-badge">
            <span class="status-dot"></span>
            ${escHtml(meta.statusLabel)}
          </span>
        </div>

        <section class="section">
          <div class="section-title">Contact Information</div>
          <div class="grid-2">
            ${field("Email", meta.email)}
            ${field("Phone", meta.phone)}
            ${field("Property Interest", meta.property)}
            ${field("Address", meta.address)}
          </div>
        </section>

        <section class="section">
          <div class="section-title">Lead Details</div>
          <div class="grid-3">
            ${field("Source", meta.sourceTypeName)}
            ${field("Property Type", meta.propertyTypeName)}
            ${field("Lead Created", meta.startDate)}
          </div>
        </section>

        <section class="section">
          <div class="section-title">Assignment & Follow-up</div>
          <div class="grid-2">
            ${field("Assigned To", meta.employeeName)}
            ${field("Employee Email", meta.employeeEmail)}
            ${field("Assigned On", meta.assignedDate)}
            ${field("Follow-up Date", meta.followUpDate)}
            ${field("Next Update", meta.nextUpdate)}
          </div>
        </section>

        <section class="section">
          <div class="section-title">Conversation Notes</div>
          <div class="comments-box">${meta.comments ? escHtml(meta.comments) : '<span class="comments-empty">No conversation notes recorded.</span>'}</div>
        </section>
      </main>

      <footer class="footer">
        <span class="footer-note">Confidential — for internal use by ${escHtml(agencyName || "authorized staff")} only. Do not share without consent.</span>
        <span>Generated ${escHtml(generatedAt)}</span>
      </footer>
    </div>
  </body>
</html>`;
}

export function openLeadPrintReport(leadassigned, agency = {}) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    window.alert("Please allow pop-ups to download or print the lead report.");
    return;
  }
  printWindow.document.write(buildLeadPrintHtml(leadassigned, agency));
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
}

export async function printLeadReport(leadassigned, clientId, fetchAgency) {
  let agency = {};
  if (clientId && fetchAgency) {
    try {
      agency = (await fetchAgency(clientId)) || {};
    } catch (error) {
      console.error("Error fetching agency for print:", error);
    }
  }
  openLeadPrintReport(leadassigned, agency);
}
